import { useTranslation } from "@i18n";
import { useState } from "react";
import { SignTypeValues } from "../../cubeComponent/constants";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import DrawSignature from "../UserSignature/DrawSignature";
import ImportSignature from "../UserSignature/ImportSignature";
import { useSelector } from "react-redux";
import { RootState } from "@Reducers/index";

/** One entry of the sign-type dropdown. */
export type SignTypeOption = { name: string; value: string };

/** What a signature capture publishes: the drawn/imported image and its file type. */
export type SignatureDocument = { base64ImageStr: string; fileType?: string };

export type SignatureProps = {
  dataSource?: SignTypeOption[];
  defaultSelectedSignType?: string;
  /**
   * Fired twice over with different shapes: with the picked sign-type value when the dropdown
   * changes, and with the captured document once a signature is drawn or imported.
   */
  onSuccess: (value: string | SignatureDocument | undefined) => void;
  acceptFileType?: string;
  showDropDown?: boolean;
};

function Signature({
  dataSource,
  defaultSelectedSignType,
  onSuccess,
  acceptFileType,
  showDropDown = true,
}: SignatureProps) {
  const { t } = useTranslation();
  const [selectedSignType, setSelectedSignType] = useState(defaultSelectedSignType);
  const userDetails = useSelector((state: RootState) => state.userDetails);

  const changeSignTypeHandler = (_event: unknown, newValue: SignTypeOption | null) => {
    const value = newValue?.value;
    onSuccess(value);
    setSelectedSignType(value);
  };

  const handleSignDocChange = async ({ base64ImageStr, fileType }: SignatureDocument) => {
    onSuccess({ base64ImageStr, fileType });
  };

  return (
    <div className="flex gap-5 flex-col w-full">
      {showDropDown && (
        <div className="flex">
          <AutoComplete
            options={dataSource}
            valueField="value"
            getOptionLabel={(option) => option.name || ""}
            value={selectedSignType}
            onChange={changeSignTypeHandler}
            sx={{ width: "100%" }}
            slotProps={{
              paper: { sx: { width: "100%" } },
            }}
            renderInput={(params) => <TextFieldComponent {...params} />}
          />
        </div>
      )}
      <div className="flex w-full">
        {selectedSignType === SignTypeValues.ATTACH_SIGNED_DOCUMENT && (
          <ImportSignature handleSignDocChange={handleSignDocChange} acceptFileType={acceptFileType} />
        )}
        {selectedSignType === SignTypeValues.DRAW_ON_SCREEN && (
          <DrawSignature drawSignatureCallback={handleSignDocChange} defaultImage={userDetails.signatureUrl} />
        )}
        {selectedSignType === SignTypeValues.IMPORT_PROFILE_SIGNATURE &&
          (userDetails.signatureUrl ? (
            <img
              className="h-36 w-36 border-2 rounded-lg border-button_light"
              src={userDetails.signatureUrl}
              alt="Signature"
            />
          ) : (
            <p className="text-red-600">{t("Signature not found, please add to profile for import")}</p>
          ))}
      </div>
    </div>
  );
}
export default Signature;
